/**
 * The display-currency policy, in one place.
 *
 * Each market allows a fixed set of currencies, and the first entry is its
 * default:
 *
 *   • US — USD only, locked.
 *   • GB — GBP only, locked.
 *   • CA — CAD or USD.
 *   • everyone else (importers) — USD or EUR.
 *
 * This lived in three separate copies — one normalising writes, one deriving
 * what to display, one listing the options in Settings — and they disagreed.
 * Reading and writing through different copies is what pinned a signed-out
 * visitor to USD: the write normalised against their delivery country and
 * stored GBP, the read resolved against their geo and mapped GBP back to USD,
 * so the switcher wrote a value it could never show and looked dead.
 *
 * Anything that stores, displays, or offers a currency must go through here.
 */
export type CurrencyPreference = "USD" | "EUR" | "CAD" | "GBP";

const IMPORTER_CURRENCIES: readonly CurrencyPreference[] = ["USD", "EUR"];

/**
 * Currencies this market may display, most-preferred first.
 * A single entry means the market is locked to it.
 */
export function currencyOptionsForCountry(
  country: string | null | undefined,
): readonly CurrencyPreference[] {
  if (country === "US") return ["USD"];
  if (country === "GB") return ["GBP"];
  if (country === "CA") return ["CAD", "USD"];
  return IMPORTER_CURRENCIES;
}

/** True when the market offers no choice, so its switcher is inert. */
export function isCurrencyLockedForCountry(
  country: string | null | undefined,
): boolean {
  return currencyOptionsForCountry(country).length === 1;
}

/**
 * Coerce a stored preference to something this market can actually show.
 * Anything invalid falls back to the market's default.
 */
export function normalizeCurrencyForCountry(
  country: string | null | undefined,
  currency: CurrencyPreference,
): CurrencyPreference {
  const options = currencyOptionsForCountry(country);
  return options.includes(currency) ? currency : options[0];
}

/**
 * The currency a switcher moves to next. Cycles the market's options, so a
 * locked market simply stays where it is instead of naming a currency it
 * would refuse to store.
 */
export function nextCurrencyForCountry(
  country: string | null | undefined,
  currency: CurrencyPreference,
): CurrencyPreference {
  const options = currencyOptionsForCountry(country);
  const current = options.indexOf(normalizeCurrencyForCountry(country, currency));
  return options[(current + 1) % options.length];
}

/**
 * Which country governs the display currency.
 *
 * Signed in, the delivery country is authoritative. For a guest it is not:
 * the delivery-country cookie outlives a sign-out, so honouring it would let
 * a previous account's market keep locking a visitor who no longer has one.
 * Guests key off fresh edge geo instead — a Canadian IP sees CAD, a US IP
 * sees USD-only, anyone else gets the neutral importer pair.
 */
export function currencyGovernorCountry({
  isSignedIn,
  deliveryCountry,
  geoCountry,
}: {
  isSignedIn: boolean;
  deliveryCountry: string | null | undefined;
  geoCountry: string | null | undefined;
}): string | null | undefined {
  return isSignedIn ? deliveryCountry : geoCountry;
}

export function currencySymbol(currency: CurrencyPreference): string {
  if (currency === "EUR") return "€";
  if (currency === "CAD") return "C$";
  if (currency === "GBP") return "£";
  return "$";
}
